import type { StateAction } from "@entrilia/shared/types";
import type {
  FundPortalCoreBrandingSettings,
  FundPortalCoreBrandingSettingsUpdate,
  RelativeLogoSize,
  SignOnSettings,
  UpdateSignOnSettingsRequest,
} from "../../../../api/types/investorPortalTypes";

export interface SavedPortalStyleSettings {
  portalSettings: FundPortalCoreBrandingSettings;
  signOnSettings: SignOnSettings;
}

export const logoTypeHeights: Record<RelativeLogoSize, number> = {
  Small: 40,
  Medium: 48,
  Large: 56,
};

export interface PortalStyleSettings {
  // Portal branding settings
  appBarColor: string;
  appBarTextColor: string;
  mainColor: string;
  logoTypeUrl: string;
  logoTypeFile: File | undefined;
  logoTypeSize: RelativeLogoSize;
  logoMarkUrl: string;
  logoMarkFile: File | undefined;
  faviconUrl: string;
  faviconFile: File | undefined;
  bannerFontColor: string;
  bannerUrl: string;
  bannerFile: File | undefined;
  // Sign-in page settings
  clientLogoUrl: string | undefined;
  clientLogoFile: File | undefined;
  primaryThemeColor: string;
  useBackgroundImage: boolean;
  backgroundColor: string;
  backgroundImageUrl: string | undefined;
  backgroundImageFile: File | undefined;
}

export const getInitialPortalStyleSettings = (): PortalStyleSettings => ({
  appBarColor: "",
  appBarTextColor: "",
  mainColor: "",
  logoTypeUrl: "",
  logoTypeFile: undefined,
  logoTypeSize: "Medium",
  logoMarkUrl: "",
  logoMarkFile: undefined,
  faviconUrl: "",
  faviconFile: undefined,
  bannerFontColor: "",
  bannerUrl: "",
  bannerFile: undefined,
  clientLogoUrl: "",
  clientLogoFile: undefined,
  primaryThemeColor: "",
  useBackgroundImage: false,
  backgroundColor: "#ffffff",
  backgroundImageUrl: "",
  backgroundImageFile: undefined,
});

const getChangedColor = (color: string | undefined, savedColor: string | undefined): string | undefined =>
  color?.toUpperCase() === savedColor?.toUpperCase() ? undefined : color;

export const getBrandingUpdateRequest = (
  state: PortalStyleSettings,
  savedPortalSettings: FundPortalCoreBrandingSettings
): FundPortalCoreBrandingSettingsUpdate => ({
  appBarColor: getChangedColor(state.appBarColor, savedPortalSettings.appBarColor),
  appBarTextColor: getChangedColor(state.appBarTextColor, savedPortalSettings.appBarTextColor),
  mainColor: getChangedColor(state.mainColor, savedPortalSettings.mainColor),
  logoTypeSize: state.logoTypeSize === savedPortalSettings.logoTypeSize ? undefined : state.logoTypeSize,
  faviconUrlImage: state.faviconFile,
  logoTypeUrlImage: state.logoTypeFile,
  logoMarkUrlImage: state.logoMarkFile,
  bannerFontColor: getChangedColor(state.bannerFontColor, savedPortalSettings.bannerFontColor),
  bannerUrlImage: state.bannerFile,
});

export const getSignOnUpdateRequest = (
  state: PortalStyleSettings,
  savedSignOnSettings: SignOnSettings
): UpdateSignOnSettingsRequest => ({
  clientLogo: state.clientLogoFile,
  primaryThemeColor: getChangedColor(state.primaryThemeColor, savedSignOnSettings.primaryThemeColor),
  useBackgroundImage:
    state.useBackgroundImage === savedSignOnSettings.useBackgroundImage ? undefined : state.useBackgroundImage,
  backgroundColor: getChangedColor(state.backgroundColor, savedSignOnSettings.backgroundColor),
  backgroundImage: state.backgroundImageFile,
});

export const isBrandingStateDirty = (
  state: PortalStyleSettings,
  savedPortalSettings: FundPortalCoreBrandingSettings
): boolean => {
  const brandingRequest = getBrandingUpdateRequest(state, savedPortalSettings);
  return Object.values(brandingRequest).some((value) => value !== undefined);
};

export const isSignOnStateDirty = (state: PortalStyleSettings, savedSignOnSettings: SignOnSettings): boolean => {
  const signOnRequest = getSignOnUpdateRequest(state, savedSignOnSettings);
  return Object.values(signOnRequest).some((value) => value !== undefined);
};

// Actions

export const initFromSavedSettingsAction =
  (savedSettings: SavedPortalStyleSettings): StateAction<PortalStyleSettings> =>
  (state) => ({
    ...state,
    appBarColor: savedSettings.portalSettings.appBarColor,
    appBarTextColor: savedSettings.portalSettings.appBarTextColor,
    mainColor: savedSettings.portalSettings.mainColor,
    logoTypeUrl: savedSettings.portalSettings.logoTypeUrl,
    logoTypeFile: undefined,
    logoTypeSize: savedSettings.portalSettings.logoTypeSize,
    logoMarkFile: undefined,
    logoMarkUrl: savedSettings.portalSettings.logoMarkUrl,
    faviconUrl: savedSettings.portalSettings.faviconUrl,
    faviconFile: undefined,
    bannerFontColor: savedSettings.portalSettings.bannerFontColor,
    bannerUrl: savedSettings.portalSettings.bannerUrl,
    bannerFile: undefined,
    clientLogoUrl: savedSettings.signOnSettings.clientLogoUrl,
    clientLogoFile: undefined,
    primaryThemeColor: savedSettings.signOnSettings.primaryThemeColor ?? savedSettings.portalSettings.mainColor,
    useBackgroundImage: savedSettings.signOnSettings.useBackgroundImage,
    backgroundColor: savedSettings.signOnSettings.backgroundColor ?? "#ffffff",
    backgroundImageUrl: savedSettings.signOnSettings.backgroundImageUrl,
    backgroundImageFile: undefined,
  });

export const updateStyleSettingAction =
  (update: Partial<PortalStyleSettings>): StateAction<PortalStyleSettings> =>
  (state) => ({
    ...state,
    ...update,
  });
